import { useState, useRef, useEffect } from "react";
import imageStorage from '../../services/imageStorage';
import { useAuth } from "../../context/AuthContext";
import ImagePreviewModal from './components/ImagePreviewModal';

function ProfileHeader({ userData = {}, onUpdateUserData, showNotification }) {
  const { user, updateUserPhoto } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const defaultUserData = {
    name: '',
    email: '',
    bio: '',
    profileImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  };

  const safeUserData = {
    ...defaultUserData,
    ...userData
  };

  // States
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    name: safeUserData.name,
    email: safeUserData.email,
    bio: safeUserData.bio
  });
  const [errors, setErrors] = useState({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    aspect: 1 / 1,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localProfileImage, setLocalProfileImage] = useState(safeUserData.profileImage);

  // Refs
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    async function loadProfileImage() {
      if (user?.uid) {
        try {
          const savedImage = await imageStorage.getImage(user.uid);
          if (savedImage) {
            setLocalProfileImage(savedImage);
            
            // Update local state without showing notification on initial load
            onUpdateUserData({ profileImage: savedImage }, true); // Always pass true here to prevent notification
            
            // Update the auth context photo URL, but don't show notification
            updateUserPhoto(savedImage);
          }
        } catch (error) {
          console.error('Error al cargar la imagen de perfil:', error);
        } finally {
          // Mark initial load as complete after first attempt
          setIsInitialLoad(false);
        }
      }
    }
    if (isInitialLoad) {
      loadProfileImage();
    }
  }, [user?.uid, onUpdateUserData, updateUserPhoto, isInitialLoad]);

  // Form handling functions
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    
    if (!form.email.trim()) {
      newErrors.email = "El email es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email inválido";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onUpdateUserData(form, false);
      setIsEditing(false);
      showNotification("Perfil actualizado correctamente", "success");
    }
  };

  const handleCancel = () => {
    setForm({
      name: safeUserData.name,
      email: safeUserData.email,
      bio: safeUserData.bio
    });
    setErrors({});
    setIsEditing(false);
  };

  // Image handling functions
  const validateImage = (file) => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'La imagen no debe superar 5MB'
      };
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Solo se permiten imágenes en formato JPG, PNG o WebP'
      };
    }
    
    return { valid: true };
  };

  const checkImageDimensions = (img) => {
    return new Promise((resolve, reject) => {
      if (img.naturalWidth < 200 || img.naturalHeight < 200) {
        reject('La imagen debe tener al menos 200x200 píxeles');
        return;
      }
      
      if (img.naturalWidth > 5000 || img.naturalHeight > 5000) {
        reject('La imagen no debe exceder 5000x5000 píxeles');
        return;
      }
      
      resolve();
    });
  };

  const handleProfileImageClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadError('');
    setSelectedImage(null);
    setPreviewUrl('');
    
    const validation = validateImage(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      showNotification(validation.error, "error");
      return;
    }
    
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setSelectedImage(file);
    setIsPreviewModalOpen(true);
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return null;
    
    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          blob.name = selectedImage.name;
          resolve(blob);
        }
      }, selectedImage.type);
    });
  };

  const handleUploadImage = async () => {
    if (!selectedImage || !user?.uid) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      let imageToUpload = selectedImage;
      if (completedCrop && completedCrop.width > 0) {
        imageToUpload = await getCroppedImg();
        if (!imageToUpload) {
          throw new Error('Error al procesar la imagen');
        }
      }
      
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      try {
        const imageUrl = await imageStorage.saveImage(user.uid, imageToUpload);
        
        // First update local state
        setLocalProfileImage(imageUrl);
        
        // Then update state and auth context
        onUpdateUserData({ profileImage: imageUrl }, false);
        
        // This is the key update for Sidebar and TopBar
        console.log("Updating user photo in AuthContext", imageUrl);
        updateUserPhoto(imageUrl);
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        setIsUploading(false);
        setIsPreviewModalOpen(false);
        showNotification("Imagen de perfil actualizada correctamente", "success");
      } catch (error) {
        clearInterval(progressInterval);
        throw error;
      }
    } catch (error) {
      setUploadError(error.message || 'Error al subir la imagen');
      setIsUploading(false);
      showNotification("Error al actualizar la imagen de perfil", "error");
    }
  };

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop);
  };

  const handleRetryUpload = () => {
    setUploadError('');
    handleUploadImage();
  };

  const handleCancelUpload = () => {
    setIsPreviewModalOpen(false);
    setSelectedImage(null);
    setPreviewUrl('');
    setUploadError('');
    setUploadProgress(0);
  };

  return (
    <div className="p-6">
      <ImagePreviewModal 
        isOpen={isPreviewModalOpen}
        onClose={handleCancelUpload}
        previewUrl={previewUrl}
        imgRef={imgRef}
        crop={crop}
        setCrop={setCrop}
        handleCropComplete={handleCropComplete}
        uploadError={uploadError}
        handleRetryUpload={handleRetryUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        handleUpload={handleUploadImage}
        checkImageDimensions={checkImageDimensions}
        setUploadError={setUploadError}
      />
      <div className="flex flex-col md:flex-row">
        {/* Profile Image Section */}
        <div className="mr-6 mb-4 md:mb-0 flex flex-col items-center">
          <div className="relative group">
            <img 
              src={localProfileImage} 
              alt="Profile" 
              className="w-28 h-28 rounded-full object-cover border-4 border-[#2a2a2a]"
            />
            <button
              type="button"
              onClick={handleProfileImageClick}
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Cambiar imagen de perfil"
            >
              <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImageChange} 
              className="hidden" 
              accept="image/jpeg,image/png,image/webp"
            />
          </div>
          <p className="text-gray-400 text-sm mt-2">Click para cambiar</p>
        </div>

        {/* User Info Section */}
        <div className="flex-1">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-white">{isEditing ? form.name : safeUserData.name}</h2>
              <p className="text-gray-400">{isEditing ? form.email : safeUserData.email}</p>
            </div>
            <button
              onClick={() => isEditing ? handleSubmit() : setIsEditing(true)}
              className="text-[#ff1b6d] hover:text-[#ff1b6d]/80"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                  Nombre
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={form.name}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md bg-[#2a2a2a] border-transparent focus:border-[#ff1b6d] focus:bg-[#2a2a2a] focus:ring-0 text-white ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={form.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md bg-[#2a2a2a] border-transparent focus:border-[#ff1b6d] focus:bg-[#2a2a2a] focus:ring-0 text-white ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-300">
                  Bio
                </label>
                <textarea
                  name="bio"
                  id="bio"
                  rows="3"
                  value={form.bio}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-[#2a2a2a] border-transparent focus:border-[#ff1b6d] focus:bg-[#2a2a2a] focus:ring-0 text
-white"
                ></textarea>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-transparent border border-gray-600 text-gray-300 rounded-md hover:bg-[#252525] focus:outline-none"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#ff1b6d] text-white rounded-md hover:bg-[#ff1b6d]/90 focus:outline-none focus:ring-2 focus:ring-[#ff1b6d]/50"
                >
                  Guardar
                </button>
              </div>
            </form>
          ) : (
            <p className="text-gray-300 whitespace-pre-wrap">{safeUserData.bio}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileHeader;
